import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BellOff, BellRing } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ActionPopover } from '@/components/action-popover'
import { Switch } from '@/components/ui/switch'
import { useAuthenticatedClient } from '@/api/queries'
import { formatApiErrorMessage } from '@/lib/api-error'
import { alertSupport, useIsChatResponder } from '@/features/storefront-chat/phone-alerts-device'

/**
 * Phone alerts for store chats (USA-1483): a store chat responder turns them on per device, and
 * the backend pushes an alert when a shopper is waiting on them. The service worker (public/sw.js)
 * shows it; tapping it opens the chat. iPhones take alerts only from the dashboard added to the
 * Home Screen.
 */

function serverKeyBytes(base64url: string) {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0))
}

function useAlertsConfig(enabled: boolean) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['storefront-chats', 'alerts'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/storefront-chats/alerts')
      if (error) throw error
      return data
    },
    enabled,
    staleTime: Infinity,
  })
}

/** This device's alert subscription: whether it is on, and turning it on or off. */
function useDeviceAlerts(publicKey: string | undefined) {
  const getClient = useAuthenticatedClient()
  const [on, setOn] = useState<boolean | null>(null)
  const [pending, setPending] = useState(false)
  const [permission, setPermission] = useState(() =>
    typeof Notification === 'undefined' ? 'default' : Notification.permission,
  )

  const save = useCallback(
    async (subscription: PushSubscription) => {
      const json = subscription.toJSON()
      const client = await getClient()
      const { error } = await client.PUT('/dashboard/storefront-chats/alerts/devices', {
        body: { endpoint: json.endpoint ?? subscription.endpoint, keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' } },
      })
      if (error) throw error
    },
    [getClient],
  )
  // The check below runs once per key; a fresh sign-in token must not re-run it mid-toggle.
  const saveRef = useRef(save)
  saveRef.current = save

  useEffect(() => {
    if (!publicKey) return
    let cancelled = false
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then(async (subscription) => {
        if (cancelled) return
        setOn(!!subscription)
        // The server forgets a device the push service dropped; tell it again on every visit.
        if (subscription) await saveRef.current(subscription).catch(() => undefined)
      })
    return () => {
      cancelled = true
    }
  }, [publicKey])

  const turnOn = async () => {
    if (!publicKey) return
    setPending(true)
    try {
      const granted = await Notification.requestPermission()
      setPermission(granted)
      if (granted !== 'granted') return
      const registration = await navigator.serviceWorker.ready
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKeyBytes(publicKey) }))
      await save(subscription)
      setOn(true)
      toast.success('Alerts on for this device')
    } catch (err) {
      toast.error(formatApiErrorMessage('Alerts could not be turned on', err))
    } finally {
      setPending(false)
    }
  }

  const turnOff = async () => {
    setPending(true)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        const client = await getClient()
        await client.POST('/dashboard/storefront-chats/alerts/devices/remove', {
          body: { endpoint: subscription.endpoint },
        })
        await subscription.unsubscribe()
      }
      setOn(false)
      toast.success('Alerts off for this device')
    } catch (err) {
      toast.error(formatApiErrorMessage('Alerts could not be turned off', err))
    } finally {
      setPending(false)
    }
  }

  return { on, pending, permission, turnOn, turnOff }
}

function Hint({ label, children }: { label: string; children: string }) {
  return (
    <ActionPopover
      title={label}
      align="end"
      trigger={
        <Button variant="outline" size="sm">
          <BellOff className="h-4 w-4" /> {label}
        </Button>
      }
    >
      <p className="text-sm">{children}</p>
    </ActionPopover>
  )
}

function DeviceAlertsSwitch({ publicKey }: { publicKey: string }) {
  const { on, pending, permission, turnOn, turnOff } = useDeviceAlerts(publicKey)
  if (permission === 'denied') {
    return <Hint label="Alerts blocked">Allow notifications for USA-RS in your device settings.</Hint>
  }
  return (
    <label className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-md px-1 text-sm font-medium">
      <BellRing aria-hidden className="h-4 w-4 text-muted-foreground" />
      Alerts
      <Switch
        checked={!!on}
        disabled={on === null || pending}
        onCheckedChange={(checked) => void (checked ? turnOn() : turnOff())}
      />
    </label>
  )
}

/** Turn this device's chat alerts on or off: for store chat responders only. */
export function ChatAlertsControl() {
  const responder = useIsChatResponder()
  const [support] = useState(alertSupport)
  const { data: config } = useAlertsConfig(responder && support === 'supported')
  if (!responder || support === 'unsupported') return null
  if (support === 'install') {
    return (
      <Hint label="Turn on alerts">
        Tap Share, then Add to Home Screen. Open USA-RS from your Home Screen and turn alerts on here.
      </Hint>
    )
  }
  return config ? <DeviceAlertsSwitch publicKey={config.public_key} /> : null
}
